import { Card, Col, Row } from "antd";
import { Column } from "@ant-design/charts";
import { StarFilled } from "@ant-design/icons";
import MathUtils from "../../../../common/utils/MathUtils";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { BlockReviewsRecentKpi } from "./BlockReviewsKpis";
import { EmptyStateBox } from "../../../components/design/EmptyStateBox/EmptyStateBox";

const ChartEmpty = ({ title }: { title: string }) => (
    <div style={{
        height: 230,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 16px',
    }}>
        <EmptyStateBox
            subtle
            title={title}
            style={{ width: '100%', maxWidth: 320, background: 'transparent', border: 'none' }}
        />
    </div>
);

export const BlockReviewsGlobal = (props: {
    data: any,
    reviewsKPIs?: any,
    recentPeriod?: string,
}) => {

    const { t } = useTranslation();

    const reviewsByMonth = props.data?.numberOfReviewsByMonth || [];
    const reviewsDistribution = props.data?.reviewsDistribution || [];
    const hasReviewsByMonth = reviewsByMonth.some((d: any) => Number(d.value) > 0);
    const hasDistribution = reviewsDistribution.some((d: any) => Number(d.percentatge) > 0);
            
    const categoryNames: any = {
        'Contestadas': 'reviews.page.global.cards.replied',
        'Sin contestar': 'reviews.page.global.cards.notReplied',
    };

    const config: any = {
        isStack: true,
        xField: 'x',
        yField: 'value',
        seriesField: 'category',
        color: ['rgba(45, 164, 133, 0.92)', 'rgba(199, 21, 133, 0.72)'],
        maxColumnWidth: 36,
        columnStyle: {
            radius: [5, 5, 0, 0],
        },
        label: false,
        legend: {
            position: 'top-right',
            marker: {
                symbol: 'circle',
            },
            itemName: {
                style: {
                    fill: '#64748b',
                    fontSize: 12,
                },
            },
        },
        xAxis: {
            line: null,
            tickLine: null,
            label: {
                formatter: (text: any) => moment(text, 'YYYY-MM').format('MMM YY'),
                autoHide: true,
                autoRotate: false,
                style: {
                    fill: '#94a3b8',
                    fontSize: 11,
                },
            },
        },
        yAxis: {
            grid: {
                line: {
                    style: {
                        stroke: '#f1f5f9',
                        lineWidth: 1,
                    },
                },
            },
            label: {
                style: {
                    fill: '#94a3b8',
                    fontSize: 11,
                },
            },
        },
        tooltip: {
            shared: true,
            showMarkers: false,
            title: (title: string) => moment(title, 'YYYY-MM').format('MMMM YYYY'),
        },
        meta: {
            category: {
                formatter: (value: string | number) => {
                    const translationKey = categoryNames[value];
                    return translationKey ? t(translationKey) : value;
                },
            },
            value: {
                alias: t('reviews.page.global.cards.reviews'),
            },
        },
    };

    // Score 0 = sin dato: se mantiene el mes en el eje (hueco) pero sin barra ni etiqueta
    const scoreByMonth = (props.data?.scoreByMonth || []).map((d: any) => ({
        ...d,
        value: Number(d.value) > 0 ? Number(d.value) : null,
    }));
    const hasScoreByMonth = scoreByMonth.some((d: any) => d.value != null);

    const scoreAnnotations = scoreByMonth
        .filter((d: any) => d.value != null)
        .map((d: any) => ({
            type: 'text',
            position: [d.x, Number(d.value) / 2],
            content: `★${Number(d.value).toFixed(1)}`,
            style: {
                textAlign: 'center',
                textBaseline: 'middle',
                fill: '#475569',
                fontSize: 10,
                fontWeight: 700,
            },
        }));

    const configScore: any = {
        xField: 'x',
        yField: 'value',
        color: 'l(270) 0:rgba(183, 208, 224, 0.7) 1:rgba(123, 168, 194, 0.88)',
        maxColumnWidth: 40,
        columnStyle: {
            radius: [4, 4, 0, 0],
            stroke: 'rgba(255, 255, 255, 0.5)',
            lineWidth: 1,
        },
        appendPadding: [8, 8, 0, 8],
        label: false,
        annotations: scoreAnnotations,
        legend: false,
        xAxis: {
            line: null,
            tickLine: null,
            label: {
                formatter: (text: any) => moment(text, 'YYYY-MM').format('MMM YY'),
                autoHide: true,
                autoRotate: false,
                style: {
                    fill: '#94a3b8',
                    fontSize: 11,
                },
            },
        },
        yAxis: {
            minLimit: 1,
            grid: {
                line: {
                    style: {
                        stroke: '#f1f5f9',
                        lineWidth: 1,
                    },
                },
            },
            label: {
                style: {
                    fill: '#94a3b8',
                    fontSize: 11,
                },
            },
        },
        tooltip: {
            showMarkers: false,
            title: (title: string) => moment(title, 'YYYY-MM').format('MMMM YYYY'),
        },
        meta: {
            value: {
                alias: 'Puntuación',
            },
        },
    };

    return (
        <>
            <Row gutter={36} style={{ marginBottom: '20px' }}>
                <Col span={16}>
                    <Card bodyStyle={{ backgroundColor: 'white', borderRadius: '25px', height: '327px' }} style={{ border: 'none' }}>
                        <div className="title-charts">
                            <h1 >{t('reviews.page.global.cards.reviews')}</h1>
                        </div>
                        <div style={{ padding: '12px 20px 8px 16px' }}>
                            {hasReviewsByMonth ? (
                                <Column data={reviewsByMonth} height={230} {...config} />
                            ) : (
                                <ChartEmpty title="No hay reseñas en el periodo seleccionado" />
                            )}
                        </div>
                    </Card>
                </Col>
                <Col span={8}>
                    <Card bodyStyle={{ backgroundColor: 'white', borderRadius: '25px', height: '327px' }} style={{ border: 'none' }}>
                        <div className="title-charts">
                            <h1 >{t('reviews.page.global.cards.reviewDistribution')}</h1>
                        </div>
                        {hasDistribution ? (
                            <div className="review-distribution-content" style={{ gap: 16, maxWidth: '100%', padding: '22px 28px 8px' }}>
                                {reviewsDistribution.map((review: any) => {
                                    const pct = MathUtils.round(100 * review.percentatge, 0);
                                    return (
                                        <div
                                            key={review.stars}
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: '52px minmax(0, 1fr) 40px',
                                                alignItems: 'center',
                                                gap: 12,
                                            }}
                                        >
                                            <div style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 5,
                                                color: '#64748b',
                                                fontSize: 13,
                                                fontWeight: 600,
                                                fontVariantNumeric: 'tabular-nums',
                                            }}>
                                                <span>{review.stars}</span>
                                                <StarFilled style={{ color: '#7b93a8', fontSize: 12 }} />
                                            </div>
                                            <div style={{
                                                height: 8,
                                                borderRadius: 999,
                                                background: '#f1f5f9',
                                                overflow: 'hidden',
                                            }}>
                                                <div style={{
                                                    height: '100%',
                                                    width: `${pct}%`,
                                                    borderRadius: 999,
                                                backgroundColor: '#7b93a8',
                                                transition: 'width 0.35s ease',
                                            }} />
                                        </div>
                                        <span style={{
                                            textAlign: 'right',
                                            fontSize: 13,
                                            fontWeight: 600,
                                            fontVariantNumeric: 'tabular-nums',
                                            color: pct === 0 ? '#94a3b8' : '#334155',
                                        }}>
                                            {pct}%
                                        </span>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <ChartEmpty title="No hay reseñas en el periodo seleccionado" />
                        )}
                    </Card>
                </Col>
            </Row>
            <Row gutter={36}>
                <Col span={16}>
                    <Card bodyStyle={{ backgroundColor: 'white', borderRadius: '25px', height: '327px' }} style={{ border: 'none' }}>
                        <div className="title-charts">
                            <h1 >{t('reviews.page.global.cards.reputationHistory')}</h1>
                        </div>
                        <div style={{ padding: '12px 20px 8px 16px' }}>
                            {hasScoreByMonth ? (
                                <Column data={scoreByMonth} height={230} {...configScore} />
                            ) : (
                                <ChartEmpty title="No hay datos de reputación en el periodo seleccionado" />
                            )}
                        </div>
                    </Card>
                </Col>
                <Col span={8}>
                    {props.reviewsKPIs && props.recentPeriod && (
                        <BlockReviewsRecentKpi
                            tall
                            reviewsKPIs={props.reviewsKPIs}
                            recentPeriod={props.recentPeriod}
                        />
                    )}
                </Col>
            </Row>
        </>
    )
}